import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';
import axiosRetry from 'axios-retry';
import { globalConfig } from 'src/configuration/config';
import {
  getAccessToken,
  requestTokenRefresh,
  waitForAccessToken,
} from './embed-auth';

const MAX_AUTH_RETRIES = 3;

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
}

/** Talks to wio.suitability.api (preview + create test). */
export const suitabilityAxiosClient = axios.create();

/** Talks to wio.api for shared catalog data (e.g. /risks). Same bearer token. */
export const wioApiAxiosClient = axios.create();

const requestInterceptor = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  // The parent relays the token via postMessage; wait for it on first load.
  const token = getAccessToken() ?? (await waitForAccessToken());
  config.headers.Authorization = `bearer ${token}`;
  return config;
};

const responseErrorHandler =
  (client: AxiosInstance) =>
  async (error: unknown): Promise<AxiosResponse> => {
    if (isAxiosError(error) && error.config && error.response?.status === 401) {
      const originalRequest = error.config as RetryableRequestConfig;
      const retryCount = originalRequest._retryCount ?? 0;

      if (retryCount < MAX_AUTH_RETRIES) {
        originalRequest._retryCount = retryCount + 1;
        const newToken = await requestTokenRefresh();
        originalRequest.headers.Authorization = `bearer ${newToken}`;
        return client(originalRequest);
      }
    }

    return Promise.reject(error);
  };

/** Wires the embed-auth bearer + 401-refresh interceptors and retry onto a client. */
const setupAuthedClient = (client: AxiosInstance): void => {
  client.interceptors.request.use(requestInterceptor);
  client.interceptors.response.use(
    (response) => response,
    responseErrorHandler(client),
  );

  axiosRetry(client, {
    retries: 3,
    retryDelay: (retryCount) => retryCount * 500,
  });
};

export const axiosClientInit = (): void => {
  suitabilityAxiosClient.defaults.baseURL =
    globalConfig.config.suitabilityApiUrl;
  wioApiAxiosClient.defaults.baseURL = globalConfig.config.wioApiUrl;

  setupAuthedClient(suitabilityAxiosClient);
  setupAuthedClient(wioApiAxiosClient);
};
